import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '../api/axiosManager';

/**
 * Ödeme (BulutTahsilat) dashboard sekmesi Redux slice'ı.
 *
 * Veri akışı (Dashboard ile aynı hibrit desen):
 *  - İlk yükleme / tarih değişimi → REST `GET /api/admin/dashboard/payments/kpi` (fetchPaymentKpi)
 *  - Periyodik snapshot        → WS `payment:sync` → syncPayment
 *  - Anlık delta               → WS `payment:event` → applyPaymentEvent
 *
 * Backend payload: { summary, stores, channels, installments, banks, dailyTrend, hourly, failReasons }
 */

const round2 = (n) => Math.round((Number(n || 0) + Number.EPSILON) * 100) / 100;

// Delta uygulandıktan sonra oranları yeniden hesapla (backend ile aynı formül).
function recomputeRates(summary) {
  const completed = summary.completedCount || 0;
  const failed = summary.failedCount || 0;
  const started = summary.startedCount || 0;
  summary.conversionRate = started > 0 ? round2((completed / started) * 100) : 0;
  summary.transactionSuccessRate =
    completed + failed > 0 ? round2((completed / (completed + failed)) * 100) : 0;
  summary.avgAmount = completed > 0 ? round2((summary.totalAmount || 0) / completed) : 0;
}

const initialState = {
  data: null, // { summary, stores, channels, installments, banks, dailyTrend, hourly, failReasons }
  filterContext: null,
  loading: false,
  error: null,
  lastUpdated: null,
  // Ham işlem kayıtları sekmesi
  logs: {
    rows: [],
    pagination: { page: 1, pageSize: 50, total: 0, totalPages: 1 },
    eventTypes: [],
    channels: [],
    filterContext: null,
    loading: false,
    error: null,
  },
};

export const fetchPaymentKpi = createAsyncThunk(
  'payment/fetchPaymentKpi',
  async (options = {}, { rejectWithValue }) => {
    const { dateRange, dateFrom, dateTo, channel } = options;
    const params = new URLSearchParams();
    if (dateFrom && dateTo) {
      params.append('dateFrom', dateFrom);
      params.append('dateTo', dateTo);
    } else if (dateRange) {
      params.append('dateRange', dateRange);
    }
    if (channel && channel !== 'all') {
      const ch = String(channel).trim().toLowerCase();
      params.append('channel', ch === 'web' ? 'direct' : ch);
    }
    const url = `/api/admin/dashboard/payments/kpi${params.toString() ? `?${params.toString()}` : ''}`;
    try {
      const response = await api.get(url);
      if (response.data?.success && response.data?.data) {
        return {
          data: response.data.data,
          filterContext: response.data.filterContext || null,
        };
      }
      return rejectWithValue('Ödeme KPI yanıtı beklenen formatta değil');
    } catch (error) {
      return rejectWithValue(error.userMessage || error.message || 'Ödeme verileri alınamadı');
    }
  }
);

export const fetchPaymentLogs = createAsyncThunk(
  'payment/fetchPaymentLogs',
  async (options = {}, { rejectWithValue }) => {
    const { dateRange, dateFrom, dateTo, eventType, channel, search, page, pageSize } = options;
    const params = new URLSearchParams();
    if (dateFrom && dateTo) {
      params.append('dateFrom', dateFrom);
      params.append('dateTo', dateTo);
    } else if (dateRange) {
      params.append('dateRange', dateRange);
    }
    if (eventType && eventType !== 'all') params.append('eventType', eventType);
    if (channel && channel !== 'all') {
      const ch = String(channel).trim().toLowerCase();
      params.append('channel', ch === 'web' ? 'direct' : ch);
    }
    if (search) params.append('search', search);
    if (page) params.append('page', page);
    if (pageSize) params.append('pageSize', pageSize);
    const url = `/api/admin/dashboard/payments/logs?${params.toString()}`;
    try {
      const response = await api.get(url);
      if (response.data?.success) {
        return {
          rows: response.data.data || [],
          pagination: response.data.pagination || { page: 1, pageSize: 50, total: 0, totalPages: 1 },
          eventTypes: response.data.eventTypes || [],
          channels: response.data.channels || [],
          filterContext: response.data.filterContext || null,
        };
      }
      return rejectWithValue('Ödeme kayıtları yanıtı beklenen formatta değil');
    } catch (error) {
      return rejectWithValue(error.userMessage || error.message || 'Ödeme kayıtları alınamadı');
    }
  }
);

const paymentSlice = createSlice({
  name: 'payment',
  initialState,
  reducers: {
    // WS payment:sync veya REST snapshot'ı state'e uygular.
    syncPayment: (state, action) => {
      const { data, filterContext } = action.payload || {};
      if (data) state.data = data;
      if (filterContext) state.filterContext = filterContext;
      state.loading = false;
      state.error = null;
      state.lastUpdated = Date.now();
    },
    // WS payment:event — tek işlemlik anlık delta (özet sayaç + tutar).
    applyPaymentEvent: (state, action) => {
      const event = action.payload?.event || action.payload;
      if (!event || !state.data || !state.data.summary) return;
      const s = state.data.summary;
      s.startedCount = (s.startedCount || 0) + 1;
      if (event.status === 'completed') {
        s.completedCount = (s.completedCount || 0) + 1;
        s.integratedCount = (s.integratedCount || 0) + 1;
        s.totalAmount = round2((s.totalAmount || 0) + (Number(event.amount) || 0));
      } else if (event.status === 'failed') {
        s.failedCount = (s.failedCount || 0) + 1;
      }
      recomputeRates(s);
      state.lastUpdated = Date.now();
    },
    clearPayment: (state) => {
      state.data = null;
      state.filterContext = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPaymentKpi.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPaymentKpi.fulfilled, (state, action) => {
        state.data = action.payload.data;
        state.filterContext = action.payload.filterContext;
        state.loading = false;
        state.error = null;
        state.lastUpdated = Date.now();
      })
      .addCase(fetchPaymentKpi.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Ödeme verileri alınamadı';
      })
      .addCase(fetchPaymentLogs.pending, (state) => {
        state.logs.loading = true;
        state.logs.error = null;
      })
      .addCase(fetchPaymentLogs.fulfilled, (state, action) => {
        state.logs.rows = action.payload.rows;
        state.logs.pagination = action.payload.pagination;
        state.logs.eventTypes = action.payload.eventTypes;
        state.logs.channels = action.payload.channels;
        state.logs.filterContext = action.payload.filterContext;
        state.logs.loading = false;
        state.logs.error = null;
      })
      .addCase(fetchPaymentLogs.rejected, (state, action) => {
        state.logs.loading = false;
        state.logs.error = action.payload || 'Ödeme kayıtları alınamadı';
      });
  },
});

export const { syncPayment, applyPaymentEvent, clearPayment } = paymentSlice.actions;
export default paymentSlice.reducer;
